<template>
  <app-panel
    class="relative"
    data-cy-next-session-enrolment
    :suptitle="$t('course.page.next_session_enrolment.title')"
  >
    <course-session-remaining-seats-chip
      v-if="session"
      class="absolute -top-2 right-4"
      :session="session"
    />

    <template #title>
      <span class="flex items-center gap-4 text-3xl" data-cy-date>
        <template v-if="!session">
          {{
            $t(
              "course.page.next_session_enrolment.no_next_session_date_placeholder",
            )
          }}
        </template>
        <template v-else>
          {{ useCourseSessionInfo(session).formattedDate.value }}
        </template>

        <course-new-session-alert-cta
          v-if="!session"
          color="dark"
          :course="course"
          size="small"
          variant="outlined"
        />
      </span>
    </template>

    <template #subtitle>
      <course-page-session-chip-info
        v-if="session"
        :course="course"
        :session="session"
      >
        <template #append>
          <app-chip
            v-if="displayedPrice"
            class="bg-gray-50"
            size="small"
            to="#packages"
            v-html="displayedPrice"
          />
        </template>
      </course-page-session-chip-info>

      <div v-else class="flex flex-wrap gap-1">
        <app-chip class="bg-gray-50" size="small"> Paris 75009 </app-chip>

        <app-chip
          v-if="displayedPrice"
          class="bg-gray-50"
          size="small"
          to="#packages"
          v-html="displayedPrice"
        />
      </div>
    </template>

    <div class="grid gap-4">
      <course-page-enrolment-actions
        v-if="session"
        :course="course"
        :session="session"
      />

      <course-page-program-download-cta v-else :course="course" />

      <p v-if="session?.organization" class="text-center text-xs">
        <strong>{{
          $t("course.page.next_session_enrolment.related_organization_label")
        }}</strong>
        <app-image
          :alt="session.organization.name"
          class="mx-auto mt-2"
          :height="35"
          :src="session.organization.logo_url"
        />
      </p>

      <p v-else-if="subtitle" class="text-center text-xs text-subtle">
        {{ subtitle }}
      </p>
    </div>

    <template #aside-bottom>
      <course-page-privatization-callout
        v-if="!session || session?.public"
        class="m-2"
        :course="course"
      />
    </template>
  </app-panel>
</template>

<script lang="ts" setup>
import type { CoursePageView } from "~/business-areas/course/course.model";
import type { CoursePackageWithDetailsView } from "~/business-areas/course-package/composables/course-packages.hook";
import type { CourseSessionView } from "~/business-areas/course-session/course-session.model";
import CoursePagePrivatizationCallout from "~/pages/courses/[slug]/_includes/components/course-page-privatization-callout.vue";
import CoursePageSessionChipInfo from "~/pages/courses/[slug]/_includes/components/course-page-session-chip-info.vue";
import CoursePageEnrolmentActions from "~/pages/courses/[slug]/_includes/components/enrolment/course-page-enrolment-actions.vue";
import CoursePageProgramDownloadCta from "~/pages/courses/[slug]/_includes/components/program/course-page-program-download-cta.vue";

const properties = defineProps<{
  course: CoursePageView;
  packages: CoursePackageWithDetailsView[];
  session?: CourseSessionView;
}>();

const i18n = useI18n();

const displayedPrice = computed(() => {
  if (!properties.session) return;

  const price = properties.packages[0]?.price;

  if (!price) return;

  return useI18nMoney(price, { html: true, excludingTaxesSuffix: true, i18n })
    .value;
});

const { isRegisteredToCourseSession } = useLearnerProfile();

const subtitle = computed(() => {
  if (!properties.session) return;

  if (isRegisteredToCourseSession(properties.session.id)) return;

  return i18n.t("course.page.registration_limit_date", {
    date: useI18nDate(properties.session.registrationLimitDate, {
      month: "short",
      day: "numeric",
    }).value,
  });
});

if (
  import.meta.client &&
  properties.course.registration_token &&
  properties.session
) {
  const { $analytics } = useNuxtApp();
  const { register, registrationLinkFlow } = useRegistrationLink({
    registration_token: properties.course.registration_token,
    session: properties.session,
  });

  onNuxtReady(() => {
    if (registrationLinkFlow.isActive(properties.course.registration_token)) {
      register();
    } else {
      // Only trigger registration_link_viewed first time to avoid having a doubled event fired on auth callback
      $analytics.track("registration_link_viewed");
    }
  });
}

useCourseNewSessionAlert(properties.course).handleQueryParameterBasedActions();
</script>
